import { useState } from 'react';

import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import { useApplication } from '@eneco/common/application';
import env from '@eneco/common/env';
import RichText from '@eneco/components/RichText/RichText';
import {
  AccessTypeDto,
  CrmAccountClaimDto,
  ImpersonationRequest,
  ImpersonationTypeDto,
  postEnecoBeXapiSiteApiV1ImpersonationInitiate,
  PostEnecoBeXapiSiteApiV1ImpersonationInitiateResponse,
} from '@eneco/dc-be/client';
import { typedYupResolver } from '@eneco/hooks/form';
import { useFormatter } from '@eneco/i18n';
import {
  Box,
  Button,
  Card,
  Form,
  Grid,
  Heading,
  InputText,
  NotificationBox,
  PageGrid,
  Stack,
  TextLink,
} from '@eneco/sparky';
import { useContent } from '@sitecore/common';
import { ImpersonationLandingRendering } from '@sitecore/types/ImpersonationLanding';
import { CrmAccountWithRole, Impersonation } from '@types-be/next-auth';
import { FormProvider, useForm } from 'react-hook-form';
import { StringParam, useQueryParam } from 'use-query-params';
import * as yup from 'yup';

import DataErrorNotification from '../../../components/DataErrorNotification';
import logout from '../../../utils/auth/logout';

function mapImpersonationType(data: PostEnecoBeXapiSiteApiV1ImpersonationInitiateResponse | undefined) {
  if (data?.data?.impersonationType === 'Email') {
    return 'Email';
  } else {
    if (data?.data?.impersonationType === 'Account') {
      return 'CrmAccountNumber';
    }
    return 'None';
  }
}

function mapImpersonationRole(data: PostEnecoBeXapiSiteApiV1ImpersonationInitiateResponse | undefined) {
  if (data?.data?.impersonationRole === 'Read') {
    return 'reader';
  } else {
    if (data?.data?.impersonationRole === 'Write') {
      return 'editor';
    }
  }
  return 'reader';
}

const mapToCrmAccountWithRole: (value: CrmAccountClaimDto) => CrmAccountWithRole = value => ({
  crmAccountNumber: value.crmAccountNumber!,
  alias: value.alias!,
  role: mapRole(value.accessType)!,
});

const mapRole = (accessType: AccessTypeDto | undefined) => {
  if (accessType)
    switch (accessType) {
      case 'Read': {
        return 'reader';
      }
      case 'Write': {
        return 'editor';
      }
      case 'Owner': {
        return 'owner';
      }
    }
  return undefined;
};

const parseImpersonationType = (impersonationFor: string): ImpersonationTypeDto => {
  if (impersonationFor.includes('@')) {
    return 'Email';
  } else {
    return 'Account';
  }
};

interface FormFields {
  impersonationFor: string;
}

type ImpersonationError = 'unauthorized' | 'notFound' | 'generic';

const REQUIRED_FIELD_MESSAGES = {
  nl: 'Vul een e-mailadres of CRM-accountnummer in',
  fr: 'Saisissez une adresse e-mail ou un numéro de compte CRM',
} as const;

const NOT_LOGGED_IN_AS_CHAMPION_MESSAGES = {
  nl: {
    title: 'Je bent niet ingelogd als champion',
    before: 'Je bent niet ingelogd als champion. ',
    linkLabel: 'Log eerst uit',
    after: ' en log opnieuw in als champion.',
  },
  fr: {
    title: "Vous n'êtes pas connecté en tant que champion",
    before: "Vous n'êtes pas connecté en tant que champion. ",
    linkLabel: "Déconnectez-vous d'abord",
    after: ' et reconnectez-vous en tant que champion.',
  },
} as const;

const ImpersonationLanding = () => {
  const { fields } = useContent<ImpersonationLandingRendering>();
  const { language } = useApplication();
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<ImpersonationError | null>(null);
  const [impersonationFor, setImpersonationFor] = useQueryParam('for', StringParam, {
    updateType: 'replaceIn',
  });
  const { format } = useFormatter();

  const { impersonate, setAccounts, switchAccount } = useSelfServiceAccount();

  const formSchema = yup.object({
    impersonationFor: yup
      .string()
      .required(language === 'nl' ? REQUIRED_FIELD_MESSAGES.nl : REQUIRED_FIELD_MESSAGES.fr),
  });

  const formMethods = useForm<FormFields>({
    resolver: typedYupResolver<FormFields>(formSchema),
    defaultValues: { impersonationFor: impersonationFor ?? '' },
  });

  const {
    formState: { errors },
    handleSubmit,
    register,
    watch,
  } = formMethods;

  const watchedImpersonationFor = watch('impersonationFor');

  const onSubmit = async (value: FormFields) => {
    try {
      setLoading(true);
      setError(null);
      setImpersonationFor(value.impersonationFor);
      const impersonationType = parseImpersonationType(value.impersonationFor);
      const body: ImpersonationRequest = { type: impersonationType, value: value.impersonationFor };
      const requestOption = { body };

      const { response, data } = await postEnecoBeXapiSiteApiV1ImpersonationInitiate(requestOption);

      if (!response.ok) {
        if (response.status === 403 || response.status === 401) {
          setError('unauthorized');
        } else if (response.status === 404) {
          setError('notFound');
        } else {
          setError('generic');
        }
      } else {
        const impersonateData: Impersonation = {
          impersonatorSubject: data!.data!.impersonatorSubject!,
          actsAsIdentityProviderDisplayName: data!.data!.actsAsIdentityProviderDisplayName!,
          actsAsIdentityProviderId: data!.data!.actsAsIdentityProviderId!,
          type: mapImpersonationType(data),
          role: mapImpersonationRole(data),
          accounts: (data!.data!.crmAccountClaims ?? []).map(claim => mapToCrmAccountWithRole(claim))!,
        };
        impersonate(impersonateData);

        if (impersonateData.accounts.length > 0) {
          setAccounts(impersonateData.accounts);
          switchAccount({
            crmAccountNumber: impersonateData.accounts[0].crmAccountNumber,
            newAccounts: impersonateData.accounts,
            redirectTo: fields.dashboardLink.value.href,
          });
        }
      }
    } catch {
      setError('generic');
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageGrid>
      <Grid.Item gridColumn={{ initial: '1/-1', md: '4/-2', lg: '5/9' }} gridRowStart="1">
        <Box paddingY="6">
          <Stack direction="column" gap="6">
            {error === 'unauthorized' &&
              (() => {
                const message = NOT_LOGGED_IN_AS_CHAMPION_MESSAGES[language === 'nl' ? 'nl' : 'fr'];
                return (
                  <NotificationBox
                    isAlert={true}
                    variant={'error'}
                    title={message.title}
                    text={
                      <>
                        {message.before}
                        <TextLink
                          emphasis="high"
                          onClick={() => logout(undefined, `${env('FE_HOST')}/my-eneco/championlogin`)}>
                          {message.linkLabel}
                        </TextLink>
                        {message.after}
                      </>
                    }
                  />
                );
              })()}
            {error === 'notFound' && (
              <NotificationBox
                isAlert={true}
                variant={'error'}
                title={fields.cannotImpersonateErrorNotification?.value.title}
                text={fields.cannotImpersonateErrorNotification?.value.content}
              />
            )}
            {error === 'generic' && <DataErrorNotification />}
            <Card as="div" corners="rounded" elevation="S">
              <Card.Content>
                <FormProvider {...formMethods}>
                  <Form onSubmit={handleSubmit(onSubmit)}>
                    <Stack direction="column" gap="6">
                      <Heading size="S" as="h2">
                        {fields.title.value}
                      </Heading>
                      <RichText html={format(fields.description.value, { user: watchedImpersonationFor ?? '' })} />
                      <InputText
                        label={'Email/CRM account number'}
                        defaultValue={''}
                        error={errors?.impersonationFor?.message}
                        {...register('impersonationFor')}
                      />
                      <Button action="primary" size="regular" tone="onLight" type="submit" isLoading={isLoading}>
                        {fields.impersonateButtonText.value}
                      </Button>
                    </Stack>
                  </Form>
                </FormProvider>
              </Card.Content>
            </Card>
          </Stack>
        </Box>
      </Grid.Item>
    </PageGrid>
  );
};

export default ImpersonationLanding;
